import React, { useRef, useState, FunctionComponent } from "react";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import { Modal } from "@material-ui/core";
import { Avatar, Theme } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import CloseOutlined from "@material-ui/icons/CloseOutlined";
import { WFDelegateUser } from "@qpr/client-api-workflow/types";
import GenericModal from "./GenericModal";

const useStyle = makeThemedStyles("DelegateModal", (theme) => {
  return {
    modalUserOptions: {
      fontSize: "18px",
      color: "#6E7478",
    },
    header: {
      color: "#003eaa",
    },
    delegateHeaderContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: "32px",
      marginBottom: "0px",
      marginBlockEnd: "20px",
    },
    closeBtn: {
      fontSize: "32px",
      color: "#767676",
      backgroundColor: "#F1F1F2",
      borderRadius: "100%",
      cursor: "pointer",
    },
    columnContainer: {
      display: "flex",
      gap: "10px",
      marginInlineStart: "33px",
      marginInlineEnd: "33px",
      marginBlockEnd: "40px",
    },
    leftColumn: {
      width: "150px",
      color: "#003eaa",
      fontSize: "16px",
      fontWeight: "bold",
    },
    rightColumn: {
      width: "100%",
      fontSize: "16px",
    },
    textarea: {
      resize: "none",
      width: "520px",
      height: "100px",
      borderRadius: "8px",
      padding: "12px",
      borderColor: "#D1D5DB",
    },
    userSelect: {
      width: "300px",
      fontSize: "18px",
      position: "absolute",
      padding: "6px 12px 6px 12px",
      outline: "1px solid #D1D5DB",
      borderRadius: "8px",
      borderBottom: "none",
    },
    modalButton: {
      color: "white",
      display: "block",
      width: "160px",
      backgroundColor: "#003eaa",
      height: "40px",
      border: "none",
      fontSize: "16px",
      marginBottom: "16px",
      textAlign: "center",
      borderRadius: "18px",
      "&:hover": {
        backgroundColor: "#003eaa",
        color: "lightgray",
        cursor: "pointer",
      },
    },
    btnDisabled: {
      opacity: "0.5",
      backgroundColor: "#999",
      "&:hover": {
        backgroundColor: "#999",
        color: "white",
        cursor: "pointer",
      },
    },
    commentHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "8px",
      margin: "32px",
      marginBottom: "0",
    },
    commentContent: {
      margin: "32px",
      marginTop: "16px",
      backgroundColor: "#F0FAFA",
      borderRadius: "16px",
      padding: "16px",
      color: "#6E7478",
    },
    formStyle: { marginBottom: "16px" },
    buttonContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      margin: "32px",
      marginTop: "48px",
    },
  };
});

export type SelectFieldOption = {
  id: string;
  title: string | JSX.Element;
};

export type ResultValue = {
  field: FieldDefinition;
  value:
    | string
    | string[]
    | SelectFieldOption
    | SelectFieldOption[]
    | undefined;
};

export type FieldDefinition = {
  type: "textarea" | "select" | "info" | "button";
  title: string | JSX.Element;
  fieldName?: string;
  content?: JSX.Element;
  isRequired?: boolean;
  initialValue?: string;
  values?: SelectFieldOption[];
};

interface DelegateModalProps {
  open: boolean;
  title?: string;
  onClose?: VoidFunction;
  users?: WFDelegateUser[];
  fields: FieldDefinition[];
  buttonText?: string;
  onSubmit?: (values: ResultValue[]) => void;
}

const DelegateModal = (props: React.PropsWithChildren<DelegateModalProps>) => {
  const classes = useStyle();
  const buttons = React.useMemo(
    () => props.fields.filter((f) => f.type === "button"),
    [props.fields]
  );
  const fields = React.useMemo(
    () => props.fields.filter((f) => f.type !== "button"),
    [props.fields]
  );
  const menuProps = {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    getContentAnchorEl: null,
  };
  const [values, setValues] = React.useState<ResultValue[]>(() => {
    return fields.map((f) => {
      return {
        field: f,
        value: f.initialValue,
      };
    });
  });
  const isButtonDisabled = React.useMemo(() => {
    return values.filter((v) => !v.value && v.field.fieldName).length > 0;
  }, [values]);
  const onClose = () => {
    if (props.onClose) props.onClose();
  };

  return (
    <GenericModal open={props.open} onClose={props.onClose}>
      {props.children}
      <div className={classes.delegateHeaderContainer}>
        <h1 className={classes.header}>{props.title}</h1>
        <CloseOutlined className={classes.closeBtn} onClick={onClose} />
      </div>

      {fields.map((field) => {
        if (field.type === "info") {
          return (
            <div className={classes.columnContainer}>
              <div className={classes.leftColumn}>{field.title}</div>
              <div className={classes.rightColumn}>{field.content}</div>
            </div>
          );
        }

        if (field.type === "select") {
          return (
            <div className={classes.columnContainer}>
              <div className={classes.leftColumn}>{field.title}</div>
              <div className={classes.rightColumn}>
                <FormControl className={classes.formStyle}>
                  <Select
                    value={values.filter((v) => v.field === field).pop()?.value}
                    onChange={(e) => {
                      if (typeof e.target.value === "string") {
                        setValues((values) =>
                          values.map((v) => {
                            if (
                              v.field.fieldName === field.fieldName &&
                              typeof e.target.value === "string"
                            ) {
                              return {
                                ...v,
                                value: e.target.value || "",
                              };
                            }
                            return v;
                          })
                        );
                      }
                    }}
                    className={classes.userSelect}
                    MenuProps={menuProps as any}
                    displayEmpty
                    disableUnderline
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    {field.values?.map((option) => {
                      return (
                        <MenuItem
                          key={option.id}
                          className={classes.modalUserOptions}
                          value={option.id}
                        >
                          {option.title}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
            </div>
          );
        }
        if (field.type === "textarea") {
          return (
            <div className={classes.columnContainer}>
              <div className={classes.leftColumn}>{field.title}</div>
              <div className={classes.rightColumn}>
                <textarea
                  onChange={(e) => {
                    setValues((values) =>
                      values.map((val) => {
                        if (val.field.fieldName === field.fieldName) {
                          return {
                            ...val,
                            value: e.target.value,
                          };
                        }
                        return val;
                      })
                    );
                  }}
                  className={classes.textarea}
                />
              </div>
            </div>
          );
        }
        return null;
      })}

      <div className={classes.buttonContainer}>
        <button
          className={`${classes.modalButton} ${
            isButtonDisabled ? classes.btnDisabled : ""
          }`}
          disabled={isButtonDisabled}
          type="button"
          onClick={() => {
            if (props.onSubmit) {
              props.onSubmit(values);
            }
          }}
        >
          {props.buttonText}
        </button>
      </div>
    </GenericModal>
  );
};

export default DelegateModal;
